import { useEffect, useState, useRef, useMemo } from 'react';
import { tokens } from "../../theme";
import {
    useTheme, Box
} from '@mui/material';

import { createChart } from 'lightweight-charts';
import { useFetchingData } from '../../components/api';

// ================= SKELETON COMPONENT =================
function ChartSkeleton({ title, height = 240 }) {
    return (
        <div className="bg-[#111827] border border-slate-700 rounded-xl flex flex-col overflow-hidden" style={{ height }}>
            <div className="px-5 py-4 border-b border-slate-700/50 flex items-center justify-between bg-[#0f172a] shrink-0" style={{ height: 60 }}>
                <p className="text-sm text-slate-300 font-bold uppercase tracking-wide">{title}</p>
                <div className="flex items-center gap-3">
                    <div className="h-5 w-14 rounded bg-slate-700 animate-pulse" />
                    <div className="h-5 w-24 rounded bg-slate-700 animate-pulse" />
                </div>
            </div>
            <div className="relative w-full flex-1 bg-[#0f172a] overflow-hidden" style={{ height: height - 60 }}>
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    {[...Array(5)].map((_, i) => {
                        const y = 15 + (i * ((height - 60) - 15 - 25)) / 4;
                        return <line key={i} x1="0" y1={y} x2="100%" y2={y} stroke="#1e293b" strokeWidth="1" />;
                    })}
                </svg>
                <div className="absolute inset-0">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.08) 40%, rgba(125,211,252,0.18) 50%, rgba(56,189,248,0.08) 60%, transparent 100%)",
                            animation: "shimmer 1.8s ease-in-out infinite",
                        }}
                    />
                </div>
                <style>{`
                    @keyframes shimmer {
                        0%   { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                `}</style>
            </div>
        </div>
    );
}

// ================= HELPER FUNCTIONS =================
const compareDatetime = (a, b, field = 'DateTime') => {
    return new Date(a[field]) - new Date(b[field]);
};

const filterByRange = (data, range) => {
    if (!Array.isArray(data)) return [];
    if (range === 'ALL' || range === 'MAX') return data;

    const currentDate = new Date();
    let fromDate;

    switch (range) {
        case '3M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 3);
            break;
        case '6M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 6);
            break;
        case '1Y':
            fromDate = new Date(currentDate);
            fromDate.setFullYear(currentDate.getFullYear() - 1);
            break;
        case 'YTD':
            fromDate = new Date(currentDate.getFullYear(), 0, 1);
            break;
        default:
            return data;
    }

    return data.filter(item => {
        const itemDate = new Date(item.DateTime || item.Date || item.date);
        return itemDate >= fromDate && itemDate <= currentDate;
    });
};

const generateData = (data, date_column = 'DateTime', field) => {
    return data.map(d => {
        const dd = new Date(d[date_column]);
        const year = dd.getFullYear();
        const month = String(dd.getMonth() + 1).padStart(2, '0');
        const day = String(dd.getDate()).padStart(2, '0');
        return { time: `${year}-${month}-${day}`, value: parseFloat(d[field]) || 0 };
    });
};

const generateUniqueRandomColors = (count) => {
    const colors = [];
    const usedColors = new Set();
    while (colors.length < count) {
        const color = '#' + [...Array(6)].map(() => '0123456789ABCDEF'[Math.floor(Math.random() * 16)]).join('');
        if (!usedColors.has(color)) { usedColors.add(color); colors.push(color); }
    }
    return colors;
};

const base_api_url =
    process.env.NODE_ENV === "production"
        ? window.location.protocol === "https:"
            ? process.env.REACT_APP_API_HTTPS_URL
            : process.env.REACT_APP_API_HTTP_URL
        : "/application";


// ================= MAIN COMPONENT =================
const ThaiRubberChartComponent = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { __fetching_data__ } = useFetchingData();

    // ================= UI STATE =================
    const [isLoading, setIsLoading] = useState(false);
    const [symbolQuery, setSymbolQuery] = useState("");
    const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);

    const [metrics, setMetrics] = useState([
        { title: "RSS3 (BKK)", value: 78.50, change: 1.2 },
        { title: "TSR20 (SGX)", value: 162.4, change: -0.5 },
        { title: "CUP LUMP", value: 45.20, change: 0.5 },
        { title: "EXCHANGE RATE", value: "...", change: 0.0 },
    ]);

    useEffect(() => {
        const fetchRubberMetrics = async () => {
            try {
                const fxRes = await fetch('https://open.er-api.com/v6/latest/USD');
                const fxData = await fxRes.json();
                const liveUSDTHB = fxData.rates.THB;
                setMetrics(prev => [prev[0], prev[1], prev[2], { title: "USD/THB", value: liveUSDTHB.toFixed(2), change: prev[3].change }]);
            } catch (error) {
                console.error("Error fetching live data:", error);
            }
        };
        fetchRubberMetrics();
        const interval = setInterval(fetchRubberMetrics, 60000);
        return () => clearInterval(interval);
    }, []);

    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [selectedYear, setSelectedYear] = useState('ALL');
    const [paramsQuery, setParamsQuery] = useState({ symbol: "", queryType: 1 });

    const uniqueRandomColors = useRef([]);
    const chartInstanceRefs = useRef([]);
    const serieInstanceRefs = useRef({ close: {}, rubber_raw: {} });

    const [chartContainers, setChartContainers] = useState([]);
    const memoizedChartContainers = useMemo(() => chartContainers, [chartContainers]);

    // ✅ KEY FIX: flag บอกว่า chart instances พร้อมแล้ว
    const [isChartReady, setIsChartReady] = useState(false);

    const [resultClosePrice, setResultClosePrice] = useState([]);
    const [resultRubber, setResultRubber] = useState([]);

    const chart_name = ['CLOSE', 'Rubber Thai'];

    const charts_option = useMemo(() => [
        {
            crosshair: { mode: 1 },
            localization: { locale: "en-US" },
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: 'transparent' },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: { borderColor: theme.palette.divider, minimumWidth: 80 },
            timeScale: { borderColor: theme.palette.divider, visible: true },
            priceLineVisible: false,
            lastValueVisible: false,
        },
        {
            crosshair: { mode: 1 },
            localization: { locale: "en-US" },
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: 'transparent' },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: { borderColor: theme.palette.divider, minimumWidth: 80 },
            timeScale: { borderColor: theme.palette.divider, visible: true },
            priceLineVisible: false,
            lastValueVisible: false,
        }
    ], [theme]);

    const [seriesSetting] = useState({
        'mainSeries1': { color: '#7bff71', lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
        'mainSeries5': { color: '#eecb72', lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
    });

    // ================= DELETE EXISTING SERIES =================
    const deleteExitingline = () => {
        try {
            if (serieInstanceRefs.current.close) {
                Object.values(serieInstanceRefs.current.close).forEach(serie => {
                    if (chartInstanceRefs.current[0] && serie) {
                        try { chartInstanceRefs.current[0].removeSeries(serie); } catch (e) { }
                    }
                });
            }
            if (serieInstanceRefs.current.rubber_raw) {
                Object.values(serieInstanceRefs.current.rubber_raw).forEach(serie => {
                    if (chartInstanceRefs.current[1] && serie) {
                        try { chartInstanceRefs.current[1].removeSeries(serie); } catch (e) { }
                    }
                });
            }
            serieInstanceRefs.current.close = {};
            serieInstanceRefs.current.rubber_raw = {};
        } catch (error) {
            console.error('Deleting Error', error);
        }
    };

    // ================= CREATE CHART INSTANCES =================
    useEffect(() => {
        if (chartContainers.length !== 2) return;

        // reset flag ก่อนสร้างใหม่
        setIsChartReady(false);

        const chartAnnotation = (chart, text) => {
            chart.applyOptions({
                watermark: {
                    visible: true,
                    fontSize: 30,
                    horzAlign: 'center',
                    vertAlign: 'center',
                    color: theme.palette.chart?.watermark || 'rgba(255, 255, 255, 0.1)',
                    text,
                },
                rightPriceScale: { visible: true },
            });
        };

        const resizeObservers = [];

        chartContainers.forEach((container, index) => {
            const chart = createChart(container, charts_option[index] || charts_option[0]);
            chartAnnotation(chart, chart_name[index]);
            chartInstanceRefs.current[index] = chart;

            const ro = new ResizeObserver((entries) => {
                if (!entries.length) return;
                try {
                    const { width, height } = entries[0].contentRect;
                    chart.resize(width, height);
                } catch (e) { }
            });
            ro.observe(container);
            resizeObservers.push(ro);
        });

        const resizeHandler = () => {
            chartInstanceRefs.current.forEach((chart, index) => {
                if (chart && chartContainers[index]) {
                    try { chart.resize(chartContainers[index].clientWidth, chartContainers[index].clientHeight); } catch (e) { }
                }
            });
        };
        window.addEventListener('resize', resizeHandler);

        // ✅ บอกว่า chart พร้อมแล้ว
        setIsChartReady(true);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            resizeObservers.forEach(ro => ro.disconnect());
            chartInstanceRefs.current.forEach((chart) => {
                if (chart) { try { chart.remove(); } catch (e) { } }
            });
            chartInstanceRefs.current = [];
            setIsChartReady(false);
        };
    }, [memoizedChartContainers, charts_option, theme]);

    // ================= PLOT DATA =================
    const setData2Charts = () => {
        if (!Array.isArray(resultClosePrice) || resultClosePrice.length === 0) return;
        if (!Array.isArray(resultRubber) || resultRubber.length === 0) return;
        if (!chartInstanceRefs.current[0] || !chartInstanceRefs.current[1]) return;

        const filteredClosePrice = filterByRange(resultClosePrice, selectedYear);
        const filteredRubber = filterByRange(resultRubber, selectedYear);

        const rawData1 = generateData(filteredClosePrice, 'DateTime', 'Close');
        const sortedRubber = [...filteredRubber].sort((a, b) => compareDatetime(a, b, 'Date'));
        const rawData2 = generateData(sortedRubber, 'Date', 'rubber_raw_local');

        // fill missing dates
        const allDatesSet = new Set([...rawData1.map(d => d.time), ...rawData2.map(d => d.time)]);
        const allDatesArray = Array.from(allDatesSet).sort();

        const fillMissingData = (dataArray) => {
            const filledData = [];
            let lastValue = dataArray.length > 0 ? dataArray[0].value : null;
            let dataIndex = 0;
            for (const currentDate of allDatesArray) {
                if (dataIndex < dataArray.length && dataArray[dataIndex].time === currentDate) {
                    lastValue = dataArray[dataIndex].value;
                    filledData.push(dataArray[dataIndex]);
                    dataIndex++;
                } else if (lastValue !== null) {
                    filledData.push({ time: currentDate, value: lastValue });
                }
            }
            return filledData;
        };

        const finalData1 = fillMissingData(rawData1);
        const finalData2 = fillMissingData(rawData2);

        // Chart 0 — CLOSE
        if (finalData1.length > 0) {
            const series1 = chartInstanceRefs.current[0].addLineSeries(seriesSetting.mainSeries1);
            series1.setData(finalData1);
            series1.applyOptions({
                lastValueVisible: false,
                priceLineVisible: false,
                visible: paramsQuery.queryType === 1 ? false : true,
            });
            serieInstanceRefs.current.close['close'] = series1;
        }

        // Chart 1 — Rubber Thai
        if (finalData2.length > 0) {
            if (uniqueRandomColors.current.length === 0) {
                uniqueRandomColors.current = generateUniqueRandomColors(1);
            }
            const series2 = chartInstanceRefs.current[1].addLineSeries(seriesSetting.mainSeries5);
            series2.setData(finalData2);
            series2.applyOptions({
                color: seriesSetting.mainSeries5.color,
                lastValueVisible: false,
                priceLineVisible: false,
                visible: true,  // ✅ rubber ให้ visible เสมอ ไม่ขึ้นกับ queryType
            });
            serieInstanceRefs.current.rubber_raw['rubber-0'] = series2;
        }

        // sync time range
        chartInstanceRefs.current.forEach((currentChart, currentIndex) => {
            currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chartInstanceRefs.current.forEach((otherChart, otherIndex) => {
                    if (currentIndex !== otherIndex && timeRange) {
                        otherChart.timeScale().setVisibleLogicalRange(timeRange);
                    }
                });
            });
        });

        // sync crosshair
        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            chartInstanceRefs.current.forEach((currentChart, currentIndex) => {
                currentChart.subscribeCrosshairMove(param => {
                    const firstKey = Object.keys(serieInstanceRefs.current[keys_series[currentIndex]])[0];
                    if (!firstKey) return;
                    const dataPoint = getCrosshairDataPoint(serieInstanceRefs.current[keys_series[currentIndex]][firstKey], param);
                    chartInstanceRefs.current.forEach((otherChart, otherIndex) => {
                        if (currentIndex !== otherIndex) {
                            for (const key_other_serie in serieInstanceRefs.current[keys_series[otherIndex]]) {
                                if (Object.hasOwnProperty.call(serieInstanceRefs.current[keys_series[otherIndex]], key_other_serie)) {
                                    syncCrosshair(otherChart, serieInstanceRefs.current[keys_series[otherIndex]][key_other_serie], dataPoint);
                                }
                            }
                        }
                    });
                });
            });

            chartInstanceRefs.current[0]?.timeScale().fitContent();
            chartInstanceRefs.current[1]?.timeScale().fitContent();
        } catch (error) {
            console.error('Crosshair sync error:', error);
        }
    };

    // ================= ✅ KEY FIX: TRIGGER PLOT =================
    // Plot เมื่อ: data พร้อม AND chart พร้อม (ไม่ว่าอะไรจะพร้อมก่อน)
    useEffect(() => {
        if (
            isChartReady &&
            chartContainers.length === 2 &&
            chartInstanceRefs.current[0] &&
            chartInstanceRefs.current[1]
        ) {
            deleteExitingline();
            setData2Charts();
        }
    }, [resultClosePrice, resultRubber, selectedYear, isChartReady]);

    // ================= FETCH DATA =================
    const get_data = async (symbolToFetch) => {
        setIsLoading(true);
        const targetSymbol = symbolToFetch !== undefined ? symbolToFetch : paramsQuery.symbol;
        const values = {
            symbol: targetSymbol,
            queryType: targetSymbol ? 2 : 1,
        };

        try {
            const response = await __fetching_data__({
                url: `${base_api_url}/rubber_test?${new URLSearchParams(values)}`,
                method: "GET"
            });
            if (response.status && response.data) {
                setResultClosePrice(response.data.close_price || []);
                setResultRubber(response.data.rubber || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setTimeout(() => setIsLoading(false), 500);
        }
    };

    // initial load
    useEffect(() => {
        get_data();
    }, []);

    // ================= CROSSHAIR HELPERS =================
    function syncCrosshair(chart, series, dataPoint) {
        if (dataPoint) {
            chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
        } else {
            chart.clearCrosshairPosition();
        }
    }

    function getCrosshairDataPoint(series, param) {
        if (!param.time) return null;
        return param.seriesData.get(series) || null;
    }

    // ================= SYMBOL LIST =================
    const [symbols, setSymbols] = useState([]);
    useEffect(() => {
        const getSymbolList = async () => {
            const response = await __fetching_data__({ url: `${base_api_url}/symbol_list`, method: "GET" });
            if (response.status && Array.isArray(response.data)) {
                const uniqueSymbols = Array.from(new Set(response.data.map(val => val.SymbolName)));
                setSymbols(uniqueSymbols);
            }
        };
        getSymbolList();
    }, []);

    const filteredSymbols = symbols.filter(s => s.toLowerCase().includes(symbolQuery.toLowerCase()));

    // ================= CHART CONTAINER REF =================
    const resChartRef = useRef(null);
    const [skeletonHeight, setSkeletonHeight] = useState(300);
    useEffect(() => {
        const updateHeight = () => {
            if (resChartRef.current) {
                setSkeletonHeight(Math.max(150, (resChartRef.current.clientHeight - 16) / 2));
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleAddChartContainer = (element) => {
        if (!chartContainers.includes(element) && element) {
            setChartContainers(prev => [...prev, element]);
        }
    };

    // ================= RENDER =================
    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#0c111b', paddingTop: '20px' }}>
            <style>{`
                @keyframes symbolBounce {
                    0%   { transform: scale(1); }
                    30%  { transform: scale(1.04); }
                    50%  { transform: scale(0.98); }
                    70%  { transform: scale(1.02); }
                    100% { transform: scale(1); }
                }
                .symbol-bounce { animation: symbolBounce 1.8s ease-in-out infinite; }
                * { scrollbar-width: none; -ms-overflow-style: none; }
                *::-webkit-scrollbar { display: none; }
            `}</style>

            <div className="w-full text-white px-6 py-2">
                <div className="max-w-[1600px] mx-auto">

                    {/* TOP CONTROL BAR */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div className="flex flex-wrap items-center gap-3 z-50">

                            {/* Symbol Dropdown */}
                            <div className="relative w-56">
                                <div className={`relative bg-[#111827] border border-slate-700 rounded-md px-4 py-3 flex items-center ${!paramsQuery.symbol && !symbolQuery ? "symbol-bounce" : ""}`}>
                                    <input
                                        value={symbolQuery}
                                        onChange={(e) => { setSymbolQuery(e.target.value); setShowSymbolDropdown(true); }}
                                        onFocus={() => setShowSymbolDropdown(true)}
                                        placeholder=""
                                        className="w-full bg-transparent outline-none text-white text-sm"
                                    />
                                    <div className="flex items-center gap-2">
                                        {(paramsQuery.symbol || symbolQuery) && (
                                            <button
                                                onClick={() => {
                                                    setSymbolQuery("");
                                                    setParamsQuery(prev => ({ ...prev, symbol: "", queryType: 1 }));
                                                    setSelectedSymbol("");
                                                    get_data("");
                                                }}
                                                className="text-slate-400 hover:text-white text-xs ml-2"
                                            >✕</button>
                                        )}
                                        <span onClick={() => setShowSymbolDropdown(!showSymbolDropdown)} className="text-slate-400 text-xs ml-2 cursor-pointer">▾</span>
                                    </div>
                                </div>

                                <label className={`absolute left-4 px-2 transition-all duration-200 pointer-events-none ${paramsQuery.symbol || symbolQuery || showSymbolDropdown ? "-top-2 text-xs bg-[#0c111b]" : "top-1/2 -translate-y-1/2 text-sm text-slate-400"}`}>
                                    Symbol*
                                </label>

                                {showSymbolDropdown && (
                                    <div className="absolute mt-2 w-full bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-50">
                                        {filteredSymbols.length > 0 ? filteredSymbols.map((item, index) => (
                                            <div
                                                key={index}
                                                onClick={() => {
                                                    setSymbolQuery(item);
                                                    setShowSymbolDropdown(false);
                                                    setParamsQuery(prev => ({ ...prev, symbol: item, queryType: 2 }));
                                                    setSelectedSymbol(item);
                                                    get_data(item);
                                                }}
                                                className="px-4 py-2 text-sm text-slate-300 hover:bg-cyan-500 hover:text-white cursor-pointer transition"
                                            >{item}</div>
                                        )) : (
                                            <div className="px-4 py-2 text-sm text-slate-500">No results</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Period Buttons */}
                        <div className="flex gap-2">
                            {["3M", "6M", "1Y", "YTD", "ALL"].map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setSelectedYear(p)}
                                    className={`px-3 py-1 text-xs rounded-md border transition-all ${selectedYear === p ? "bg-[#1f2937] border-cyan-400 text-cyan-400" : "border-slate-700 text-slate-400 hover:border-cyan-400 hover:text-cyan-400"}`}
                                >{p === "ALL" ? "MAX" : p}</button>
                            ))}
                        </div>
                    </div>

                    {/* CHARTS GRID */}
                    <div
                        ref={resChartRef}
                        className="flex flex-col gap-4"
                        style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}
                    >
                        {/* Chart 1 — CLOSE */}
                        <div className="flex-1 relative flex flex-col min-h-0">
                            {isLoading && (
                                <div className="absolute inset-0 z-10">
                                    <ChartSkeleton title="CLOSE" height={skeletonHeight} />
                                </div>
                            )}
                            <div
                                className="bg-[#111827] border border-slate-700 rounded-xl p-2 relative flex flex-col w-full h-full min-h-0"
                                style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                            >
                                <div
                                    id="chart-close"
                                    className="w-full flex-1"
                                    ref={(element) => handleAddChartContainer(element)}
                                />
                            </div>
                        </div>

                        {/* Chart 2 — Rubber Thai */}
                        <div className="flex-1 relative flex flex-col min-h-0">
                            {isLoading && (
                                <div className="absolute inset-0 z-10">
                                    <ChartSkeleton title="Rubber Thai" height={skeletonHeight} />
                                </div>
                            )}
                            <div
                                className="bg-[#111827] border border-slate-700 rounded-xl p-2 relative flex flex-col w-full h-full min-h-0"
                                style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                            >
                                <div
                                    id="chart-rubber-raw"
                                    className="w-full flex-1"
                                    ref={(element) => handleAddChartContainer(element)}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Box>
    );
};

export default ThaiRubberChartComponent;